import { IonCol, IonGrid, IonRow } from "@ionic/react";
import equal from "deep-equal";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { BooleanParam, createEnumParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import Checkbox from "@components/fields/Checkbox";
import ExpertSelect from "@components/fields/ExpertSelect";
import Select from "@components/fields/Select";
import styles from "@components/forms/AdminJourneyFilters.module.css";
import Form from "@components/forms/Form";
import { CarePlanStatusEnum, ConsultReportStatusEnum } from "@typing/Generated";

type FormData = {
  expertArchived?: boolean | null;
  expertId?: string | null;
  icrStatus?: ConsultReportStatusEnum | null;
  status?: CarePlanStatusEnum | null;
};

type Props = {
  filters: FormData;
  onChange: (filterValues: FormData) => void;
};

const CarePlansFilters = ({ filters, onChange }: Props) => {
  const intl = useIntl();

  const consultReportStatusParam = createEnumParam<ConsultReportStatusEnum>(Object.values(ConsultReportStatusEnum));

  const statusParam = createEnumParam<CarePlanStatusEnum>(Object.values(CarePlanStatusEnum));

  const [query, setQuery] = useQueryParams({
    expertArchived: withDefault({ ...BooleanParam, urlName: "carePlanArchived" }, filters.expertArchived),
    expertId: { ...StringParam, urlName: "carePlanExpertId" },
    expertName: { ...StringParam, urlName: "carePlanExpertName" },
    icrStatus: withDefault({ ...consultReportStatusParam, urlName: "carePlanIcrStatus" }, filters.icrStatus),
    status: withDefault({ ...statusParam, urlName: "carePlanStatus" }, filters.status)
  });

  const defaultValues: FormData = useMemo(() => ({ ...filters, ...query }), [filters, query]);

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange"
  });

  const expertArchived = form.watch("expertArchived");
  const expertId = form.watch("expertId");
  const icrStatus = form.watch("icrStatus");
  const status = form.watch("status");

  useEffect(() => {
    const values = { expertArchived, expertId, icrStatus, status };
    if (!equal(values, filters)) {
      setQuery({ ...query, ...values });
      onChange(values);
    }
  }, [expertArchived, expertId, filters, icrStatus, onChange, query, setQuery, status]);

  const statusOptions = useMemo(() => {
    const options = [
      CarePlanStatusEnum.OVERDUE,
      CarePlanStatusEnum.NOT_STARTED,
      CarePlanStatusEnum.UNPUBLISHED_CHANGES,
      CarePlanStatusEnum.IN_PROGRESS,
      CarePlanStatusEnum.ON_TIME
    ].map(option => ({
      label: intl.formatMessage({ id: `models.carePlan.status.${option}` }),
      value: option as string
    }));
    options.unshift({ label: intl.formatMessage({ id: "dictionary.all" }), value: "" });

    return options;
  }, [intl]);

  const consultReportStatusOptions = Object.values(ConsultReportStatusEnum).map(option => ({
    label: intl.formatMessage({ id: `models.expert.consultReportStatus.${option}` }),
    value: option
  }));

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form className={styles.form} hookForm={form} onSubmit={() => {}}>
      <IonGrid>
        <IonRow className="ion-align-items-center">
          <IonCol className="ion-margin-top" size="auto">
            <div>
              <Checkbox<FormData>
                control={form.control}
                error={form.formState.errors.expertArchived}
                name="expertArchived"
              >
                <FormattedMessage id="forms.carePlansFilters.expertArchivedLabel" />
              </Checkbox>
            </div>
          </IonCol>
          <IonCol>
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.status}
              label={intl.formatMessage({ id: "forms.carePlansFilters.statusLabel" })}
              name="status"
              options={statusOptions}
            />
          </IonCol>
          <IonCol>
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.icrStatus}
              label={intl.formatMessage({ id: "forms.carePlansFilters.consultReportStatusLabel" })}
              name="icrStatus"
              options={consultReportStatusOptions}
            />
          </IonCol>
          <IonCol>
            <ExpertSelect<FormData>
              allowAny
              control={form.control}
              error={form.formState.errors.expertId}
              initialOption={{ label: query.expertName ?? undefined, value: query.expertId ?? null }}
              label={intl.formatMessage({ id: "forms.carePlansFilters.expertLabel" })}
              name="expertId"
              onSelection={(option: { label: string }) => {
                setQuery({ ...query, ...{ expertName: option.label } });
              }}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
    </Form>
  );
};

export default CarePlansFilters;
